/* nplus-payever-finance.js — payever Lending calculator in the buy-box Finance tab.
   Germany + block setting payever_finance_enabled only (see nplus-buybox-combined.liquid);
   loaded only when that branch renders.

   Mounts into [data-nplus-payever] the first time the Finance tab is shown (never while
   hidden — a hidden mount measures 0 width), with the selected size's price. On
   nplus:bike-variant-change it re-mounts, debounced, destroying the previous widget first;
   a change while the tab is hidden is applied when it's shown again. The host keeps its
   height across a re-mount so the tab doesn't jump. payever's script is loaded once per page.

   payever's "Get Code" embed (Lending widget, zinia_lending_de) is wired in through ADAPTER
   below. Tests swap in window.nplusPayeverAdapter. */
(function () {
  'use strict';

  var DEBOUNCE_MS = 250;

  // ---- payever "Get Code" embed (Lending widget) -----------------------------------------
  // Config as payever generated it, except styles.dimensions.minWidth (380 → 300: 380 overflowed
  // the buy box on a 390px iPhone). purchase.amount (the selected size, EUR major units — payever
  // shows 30050 as 30.050,00 €) and the mount target are ours. load(): add embed.js once per page
  // as a script tag (see loadEmbed).
  // mount(el, opts): createWidget + mount into a fresh child of `el`; opts.amount is the size's
  // price in EUR major units (e.g. 4999). destroy(el): clear the host.
  var PAYEVER_EMBED_URL = 'https://checkout.payever.org/widget/612524/embed.js';
  function payeverConfig() {
    return {
      mode: 'express',
      type: 'dropdownCalculator',
      environment: 'production',
      locale: 'de',
      channelSet: {
        id: 'd99a3646-44c2-4b5b-8965-23f69a60b34a'
      },
      styles: {
        dimensions: {
          minWidth: 300,
          maxWidth: 500
        },
        page: {
          backgroundColor: '#ffffff',
          lineColor: '#eeeeee',
          textPrimaryColor: '#333333',
          textSecondaryColor: '#333333',
          linkColor: '#333333'
        },
        input: {
          backgroundColor: '#ffffff',
          borderColor: '#e8e8e8',
          textPrimaryColor: '#333333',
          textSecondaryColor: '#555555'
        },
        button: {
          primary: {
            backgroundColor: '#e8e8e8',
            textColor: '#333333'
          }
        },
        checkoutPlacement: 'rightSidebar',
        alignment: 'left',
        theme: 'light'
      },
      payments: [
        {
          paymentMethod: 'zinia_lending_de',
          enabled: true,
          connectionId: 'ba01af94-0e8b-44f6-9513-df2355527e4b'
        }
      ]
    };
  }

  // embed.js is loaded through a script tag the page adds at runtime, never through a dynamic
  // module load in this file: Shopify's asset minifier rewrote that into a CommonJS call that
  // doesn't exist in the browser (a "… is not defined" error). embed.js itself is module code
  // (its last statement is a module-only declaration; it sets window.payever), so the tag is type="module" — a classic
  // tag would fail to parse it. Memoised per page; reuses a tag with the same src if one is
  // already in the DOM. An existing window.payever alone doesn't count: it can come from the
  // finance-express app bundle rather than this checkout embed.
  var embedLoading = null;
  function findEmbedScript() {
    for (var i = 0; i < document.scripts.length; i++) {
      if (document.scripts[i].src === PAYEVER_EMBED_URL) return document.scripts[i];
    }
    return null;
  }
  function hasCreateWidget() { return !!(window.payever && typeof window.payever.createWidget === 'function'); }
  function loadEmbed() {
    if (embedLoading) return embedLoading;
    embedLoading = new Promise(function (resolve, reject) {
      var done = function () {
        if (hasCreateWidget()) resolve();
        else reject(new Error('payever embed.js loaded without window.payever'));
      };
      var script = findEmbedScript();
      if (script) {
        // Someone else's tag: already evaluated if createWidget is there, otherwise wait for it.
        if (script.getAttribute('data-nplus-loaded') === 'true' || hasCreateWidget()) { done(); return; }
      } else {
        script = document.createElement('script');
        script.type = 'module';
        script.async = true;
        script.src = PAYEVER_EMBED_URL;
        document.head.appendChild(script);
      }
      script.addEventListener('load', function () { script.setAttribute('data-nplus-loaded', 'true'); done(); }, { once: true });
      script.addEventListener('error', function () {
        reject(new Error('payever embed.js failed to load: ' + PAYEVER_EMBED_URL));
      }, { once: true });
    });
    // A failed load can be retried on the next tab show / size change: forget it and its tag.
    embedLoading.catch(function () {
      embedLoading = null;
      var s = findEmbedScript();
      if (s && s.getAttribute('data-nplus-loaded') !== 'true' && s.parentNode) s.parentNode.removeChild(s);
    });
    return embedLoading;
  }

  var mountSeq = 0;
  var ADAPTER = {
    load: loadEmbed,
    mount: function (el, opts) {
      if (opts.currency && opts.currency !== 'EUR') return; // Lending DE quotes EUR only
      // payever mounts by selector: give each mount its own id inside the host.
      var target = document.createElement('div');
      target.id = 'nplus-payever-widget-' + (++mountSeq);
      el.appendChild(target);
      return window.payever.createWidget('drop-in', payeverConfig(), { purchase: { amount: opts.amount } })
        .then(function (widget) {
          if (target.isConnected) widget.mount('#' + target.id); // skip if a newer size cleared the host meanwhile
        });
    },
    destroy: function (el) { el.innerHTML = ''; }
  };
  // ----------------------------------------------------------------------------------------

  function adapter() { return window.nplusPayeverAdapter || ADAPTER; }

  var loading = null;
  function loadOnce() {
    if (!loading) {
      loading = Promise.resolve().then(function () { return adapter().load(); });
      loading.catch(function () { loading = null; }); // retry on the next render
    }
    return loading;
  }

  // Selected size's price in major units: the buy-box pill (kept current by nplus-buybox.js),
  // falling back to the Liquid-rendered price for single-variant products (no pills).
  function selectedAmount(host) {
    var box = host.closest('nplus-buybox');
    var pill = box ? box.querySelector('[data-nplus-variant-pill].is-selected') : null;
    var minor = parseInt(pill ? pill.getAttribute('data-price-minor') : host.getAttribute('data-amount-minor'), 10);
    return isNaN(minor) ? null : minor / 100;
  }

  function init(host) {
    if (host._nplusPayever) return;
    var state = host._nplusPayever = { mounted: false, amount: null, seq: 0, timer: null };
    var panel = host.closest('[data-nplus-tabpanel]');
    var visible = function () { return !panel || !panel.hidden; };

    function render() {
      var amount = selectedAmount(host);
      if (amount == null || (state.mounted && amount === state.amount)) return;
      var seq = ++state.seq;
      loadOnce().then(function () {
        if (seq !== state.seq) return; // a newer size change superseded this one
        if (state.mounted) {
          host.style.minHeight = host.offsetHeight + 'px'; // hold height across the swap
          try { adapter().destroy(host); } catch (e) { /* stale widget; host is cleared below */ }
          host.innerHTML = '';
          state.mounted = false;
        }
        state.mounted = true;
        state.amount = amount;
        return adapter().mount(host, {
          widgetId: host.getAttribute('data-widget-id'),
          amount: amount,
          currency: host.getAttribute('data-currency') || 'EUR',
          locale: (document.documentElement.lang || 'de').slice(0, 2)
        });
      }).catch(function (err) {
        console.error('[nplus-payever] widget failed:', (err && err.message) || err);
      });
    }

    if (visible()) render();
    if (panel && window.MutationObserver) {
      new MutationObserver(function () { if (visible()) render(); })
        .observe(panel, { attributes: true, attributeFilter: ['hidden'] });
    }
    document.addEventListener('nplus:bike-variant-change', function () {
      clearTimeout(state.timer);
      state.timer = setTimeout(function () { if (visible()) render(); }, DEBOUNCE_MS);
    });
  }

  function boot() { Array.prototype.forEach.call(document.querySelectorAll('[data-nplus-payever]'), init); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  window.nplusPayeverFinance = { init: init, DEBOUNCE_MS: DEBOUNCE_MS, ADAPTER: ADAPTER, payeverConfig: payeverConfig, PAYEVER_EMBED_URL: PAYEVER_EMBED_URL }; // for tests
})();
